import React, { useEffect, useState } from "react";
import { AlertTriangle, Calculator, Eye, EyeOff, Users } from "lucide-react";
import type { AdminProductItem } from "../hooks/useAdminProductsList";
import type {
  MetadataFieldConfig,
  MetadataTagConfig,
  MetadataTextConfig,
  ProductMetadata,
} from "../../../../types/productMetadata";
import { METADATA_FIELD_CONFIGS } from "../../../../types/productMetadata";
import type { Visibility } from "../../../../types/subscription";
import { VISIBILITY_OPTIONS } from "../../../../types/subscription";
import { formatUtcDate } from "../../../../utils/dateUtils";
import { formatNumberFixed } from "../../../../utils/numberUtils";

const VISIBILITY_CONFIG: Record<string, { label: string; icon: typeof Eye; color: string }> = {
  PUBLIC: { label: "Public", icon: Eye, color: "text-rp-green" },
  SUBSCRIBER: { label: "Subscriber", icon: Users, color: "text-rp-amber" },
  PRIVATE: { label: "Private", icon: EyeOff, color: "text-rp-grey/40" },
};

function InlineTagSelect({
  config,
  value,
  saving,
  onChange,
}: {
  config: MetadataTagConfig;
  value: string | undefined;
  saving: boolean;
  onChange: (key: keyof ProductMetadata, value: string | undefined) => void;
}) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => {
        e.stopPropagation();
        onChange(config.key, e.target.value || undefined);
      }}
      onClick={(e) => e.stopPropagation()}
      disabled={saving}
      aria-label={config.label}
      className={`bg-transparent border border-rp-separator text-xs font-bold text-center cursor-pointer focus:outline-none focus:ring-1 focus:ring-rp-amber focus:border-rp-amber rounded px-1 py-0.5 w-full transition-colors ${
        saving
          ? "opacity-50 cursor-wait"
          : !value
          ? "text-rp-grey/25"
          : "text-rp-grey/70"
      }`}
    >
      <option value="">--</option>
      {config.options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

function InlineTagTextInput({
  config,
  value,
  saving,
  onChange,
}: {
  config: MetadataTextConfig;
  value: string | undefined;
  saving: boolean;
  onChange: (key: keyof ProductMetadata, value: string | undefined) => void;
}) {
  const [draft, setDraft] = useState(value ?? "");

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  const commit = () => {
    const nextValue = draft.trim();
    if (nextValue === (value ?? "")) return;
    onChange(config.key, nextValue || undefined);
  };

  return (
    <input
      type="text"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      onClick={(e) => e.stopPropagation()}
      disabled={saving}
      aria-label={config.label}
      placeholder={config.placeholder ?? "--"}
      className={`bg-transparent border border-rp-separator text-xs font-mono text-left focus:outline-none focus:ring-1 focus:ring-rp-amber focus:border-rp-amber rounded px-1.5 py-0.5 w-40 transition-colors placeholder:text-rp-grey/25 ${
        saving ? "opacity-50 cursor-wait" : "text-rp-grey/70"
      }`}
    />
  );
}

function InlineMetadataField({
  config,
  value,
  saving,
  onChange,
}: {
  config: MetadataFieldConfig;
  value: string | undefined;
  saving: boolean;
  onChange: (key: keyof ProductMetadata, value: string | undefined) => void;
}) {
  if ("options" in config) {
    return (
      <InlineTagSelect
        config={config}
        value={value}
        saving={saving}
        onChange={onChange}
      />
    );
  }

  return (
    <InlineTagTextInput
      config={config}
      value={value}
      saving={saving}
      onChange={onChange}
    />
  );
}

interface AdminProductRowProps {
  product: AdminProductItem;
  metadata: ProductMetadata | null;
  saving: boolean;
  visibilitySaving: boolean;
  onClick: (product: AdminProductItem) => void;
  onAnalyzeClick: (product: AdminProductItem) => void;
  onRemarksClick: (product: AdminProductItem, anchor: DOMRect) => void;
  onTagChange: (
    product: AdminProductItem,
    key: keyof ProductMetadata,
    value: string | undefined
  ) => void;
  onVisibilityChange: (product: AdminProductItem, next: Visibility) => void;
}

const AdminProductRow: React.FC<AdminProductRowProps> = ({
  product,
  metadata,
  saving,
  visibilitySaving,
  onClick,
  onAnalyzeClick,
  onRemarksClick,
  onTagChange,
  onVisibilityChange,
}) => {
  const isBasket = product.productType === "basket";
  const isManagedStrategy = product.productType === "managedStrategy";
  const visibilityOptions = isManagedStrategy
    ? VISIBILITY_OPTIONS.filter((o) => o.value !== "SUBSCRIBER")
    : VISIBILITY_OPTIONS;
  const vis = VISIBILITY_CONFIG[product.visibility] ?? VISIBILITY_CONFIG.PRIVATE;
  const VisIcon = vis.icon;
  const remarkCount = product.contractRemarks.length;

  const updatedLabel = formatUtcDate(product.updatedAtUtc, {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });
  const createdLabel = formatUtcDate(product.createdAtUtc, {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });

  return (
    <tr
      onClick={() => onClick(product)}
      className="bg-rp-surface transition-colors group h-11 hover:bg-rp-surface-raised cursor-pointer"
    >
      {/* ID */}
      <td className="px-2 py-2.5 hidden lg:table-cell">
        <span className="text-xs text-rp-grey/50 font-mono">{product.id}</span>
      </td>

      {/* Type */}
      <td className="px-1 py-2.5 text-center">
        <span
          className="text-xs font-extrabold text-rp-grey/60"
          title={isManagedStrategy ? "Managed Strategy" : isBasket ? "Basket" : "Portfolio"}
        >
          {isManagedStrategy ? "M" : isBasket ? "B" : "P"}
        </span>
      </td>

      {/* Title */}
      <td className="px-2 py-2.5 max-w-[280px] relative group/title">
        <p
          className="font-bold text-rp-grey text-sm leading-none truncate"
          aria-label={product.title}
        >
          {product.title}
        </p>
        <div className="pointer-events-none absolute left-2 top-full mt-1 hidden group-hover/title:block w-max max-w-[480px] px-2 py-1 bg-black text-white text-xs font-medium rounded shadow-lg z-20 whitespace-normal break-words">
          {product.title}
        </div>
      </td>

      {/* Created */}
      <td className="px-2 py-2.5 text-right text-xs text-rp-grey/50 hidden md:table-cell">
        {createdLabel}
      </td>

      {/* Provider */}
      <td className="px-2 py-2.5 hidden md:table-cell" title={product.providerName}>
        <span className="inline-flex items-center gap-1.5 text-sm text-rp-grey/70">
          {product.providerId !== null && (
            <img
              src={`/provider-assets/${product.providerId}/logo.png`}
              alt=""
              className="w-4 h-4 rounded-full object-cover flex-shrink-0"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          )}
          {product.providerName.length > 10 ? product.providerName.slice(0, 10) + "..." : product.providerName}
        </span>
      </td>

      {/* Visibility */}
      <td className="px-2 py-2.5">
        <div className="inline-flex items-center gap-1.5">
          <VisIcon className={`w-3.5 h-3.5 flex-shrink-0 ${vis.color}`} />
          <select
            value={product.visibility}
            onChange={(e) => {
              e.stopPropagation();
              onVisibilityChange(product, e.target.value as Visibility);
            }}
            onClick={(e) => e.stopPropagation()}
            disabled={visibilitySaving}
            title="Change visibility"
            className={`bg-transparent border border-rp-separator text-[11px] font-extrabold uppercase cursor-pointer focus:outline-none focus:ring-1 focus:ring-rp-amber focus:border-rp-amber rounded px-1 py-0.5 transition-colors ${
              visibilitySaving ? "opacity-50 cursor-wait" : vis.color
            }`}
          >
            {visibilityOptions.map((opt) => (
              <option key={opt.value} value={opt.value} className="text-rp-grey bg-rp-surface">
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </td>

      {/* Longs */}
      <td className="px-2 py-2.5 text-right">
        <span className="text-xs font-mono text-rp-grey/70">{product.longCount}</span>
      </td>

      {/* Shorts */}
      <td className="px-2 py-2.5 text-right">
        <span className={`text-xs font-mono ${product.shortCount > 0 ? "text-rp-grey/70" : "text-rp-grey/25"}`}>
          {product.shortCount}
        </span>
      </td>

      {/* Options */}
      <td className="px-2 py-2.5 text-right">
        <span className={`text-xs font-mono ${product.optionCount > 0 ? "text-rp-grey/70" : "text-rp-grey/25"}`}>
          {product.optionCount}
        </span>
      </td>

      {/* Gross Exposure */}
      <td className="px-2 py-2.5 text-right">
        <span className="text-xs font-mono text-rp-grey/70">
          {formatNumberFixed(product.grossExposure, 1)}%
        </span>
      </td>

      {/* Net Exposure */}
      <td className="px-2 py-2.5 text-right">
        <span className="text-xs font-mono text-rp-grey/70">
          {product.netExposure >= 0 ? "+" : ""}
          {formatNumberFixed(product.netExposure, 1)}%
        </span>
      </td>

      {/* Investible status dot */}
      <td className="px-1 py-2.5 text-center">
        <div
          className={`w-2.5 h-2.5 rounded-full mx-auto ${
            metadata?.investible_tag === "Yes" ? "bg-rp-green" : "bg-rp-separator"
          }`}
          title={metadata?.investible_tag === "Yes" ? "Investible" : "Not investible"}
        />
      </td>

      {/* Inline metadata fields */}
      {METADATA_FIELD_CONFIGS.map((config) => (
        <td key={config.key} className="px-1 py-2.5 text-center">
          <InlineMetadataField
            config={config}
            value={metadata?.[config.key]}
            saving={saving}
            onChange={(key, val) => onTagChange(product, key, val)}
          />
        </td>
      ))}

      {/* Updated */}
      <td className="px-2 py-2.5 text-right text-xs text-rp-grey/50">{updatedLabel}</td>

      {/* Contract remarks */}
      <td className="px-2 py-2.5 text-center">
        {remarkCount > 0 ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemarksClick(product, e.currentTarget.getBoundingClientRect());
            }}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-rp-amber/30 bg-rp-amber/10 text-rp-amber text-[10px] font-bold uppercase tracking-wider hover:bg-rp-amber/20 hover:border-rp-amber/60 transition-colors"
            title={`View ${remarkCount} contract remark${remarkCount !== 1 ? "s" : ""}`}
          >
            <AlertTriangle className="w-3 h-3" />
            {remarkCount}
          </button>
        ) : (
          <span className="text-xs text-rp-grey/25" title="No contract remarks">
            --
          </span>
        )}
      </td>

      {/* Actions */}
      <td className="px-2 py-2.5 text-center">
        {!isManagedStrategy && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAnalyzeClick(product);
            }}
            className="inline-flex items-center justify-center p-1 rounded hover:bg-rp-amber/15 text-rp-grey/50 hover:text-rp-amber transition-colors"
            title="Analyze min investment"
          >
            <Calculator className="w-3.5 h-3.5" />
          </button>
        )}
      </td>
    </tr>
  );
};

export default AdminProductRow;
